// Ulture Pest — V2 PDF report engine.
//
// Implements the approved "Informe de Aplicaciones" visual spec on jsPDF v4 +
// jspdf-autotable v5. Geometry in mm, font sizes in pt. The font family is
// "helvetica" until registerSora() succeeds, then "Sora" (SemiBold→normal,
// Bold→bold) + "SoraLight" (400) — every setFont reads this.family so the doc
// degrades cleanly to Helvetica with identical geometry.

import jsPDF from "jspdf";
import autoTable, { __createTable, type RowInput, type UserOptions } from "jspdf-autotable";
import { PDF_V2, getUltureLogoDataUrl } from "./brand";
import { gradientHeaderPNG } from "./canvasHeader";
import type { ApplicationBlock, FilterChipV2, KpiCard } from "./reportBlocks";
import type { MarcaInformes } from "@/lib/marca/tipos";
import { admiteBanda, coloresMarca, coloresPdf, textoSuaveSobre, type ColoresPdf } from "@/lib/marca/paleta";

/** Alias con el que el logo de la empresa se incrusta UNA vez aunque salga en cada página. */
const LOGO_MARCA_ALIAS = "marca-logo";
/** Ídem, el logo de Ulture junto a «Hecho con Ulture Pest» en el pie de un informe con marca. */
const LOGO_ULTURE_ALIAS = "ulture-logo";

export interface BuilderV2Init {
  /**
   * La marca de la empresa («Personalizar informes», 29-09-2026): su logo, sus
   * colores y sus líneas de cabecera. `null` = el informe de siempre, idéntico.
   */
  marca: MarcaInformes | null;
  orientation?: "portrait" | "landscape";
  /** Brand title in the header band (default "Ulture Pest"). */
  brandTitle?: string;
  /** Header subtitle, e.g. "Informe de Aplicaciones · Registro de tratamientos". */
  brandSubtitle: string;
  /** Client / farm name (right side of header). */
  clientName: string;
  /** Pre-formatted date range, e.g. "01 jun 2026 – 30 jun 2026". */
  dateRange?: string;
  /** Spanish currency formatter (from useCurrencySettings). */
  formatCurrency: (n: number) => string;
  /** Number formatter (decimal comma). Defaults to es-ES. */
  formatNumber?: (n: number, decimals?: number) => string;
  /**
   * Footer: generated line + page-of labels. `madeWith` = el crédito del pie en
   * un informe CON marca («Hecho con Ulture Pest», decisión de Pablo 29-09-2026).
   */
  footer: { generated: string; page: string; of: string; madeWith?: string };
}

const S = PDF_V2.size;
type TableOpts = {
  head: RowInput[];
  body: RowInput[];
  columnStyles?: UserOptions["columnStyles"];
  didParseCell?: UserOptions["didParseCell"];
};
const LEFT = PDF_V2.margin.left;
const TOP = PDF_V2.margin.top;

export class PdfReportBuilderV2 {
  readonly doc: jsPDF;
  private y: number = TOP;
  /**
   * Cabecera de sección ESPERANDO a lo que lleva debajo (Pablo, 2026-10-22,
   * portado de Ulture Control: «una tabla nueva, en página nueva»).
   * `drawDaySection` ya no pinta en el acto: guarda el título y lo pinta el
   * siguiente bloque, que sabe cuánto mide. Así, si cabecera + tabla no caben
   * en lo que queda de página, las dos saltan JUNTAS a la siguiente — en vez
   * de dejar el título y media tabla abajo y el resto en otra hoja. jsPDF no
   * borra lo pintado, por eso hay que decidir ANTES de pintar.
   */
  private pendingSection: { title: string; pill: string } | null = null;
  private family = "helvetica";
  private lightFamily = "helvetica";
  private logoDataUrl: string | null = null;
  /** Con marca: el logo de Ulture, pequeño, junto al crédito del pie. */
  private logoUltureDataUrl: string | null = null;
  private readonly opts: BuilderV2Init;
  private readonly fmtNum: (n: number, d?: number) => string;
  /** La marca de la empresa, o null (informe de siempre). */
  private readonly marca: MarcaInformes | null;
  /** La paleta de ESTE informe: la de siempre, o la de la marca. */
  private readonly C: ColoresPdf;

  constructor(init: BuilderV2Init) {
    this.doc = new jsPDF({
      orientation: init.orientation ?? "portrait",
      unit: "mm",
      format: "a4",
    });
    this.opts = init;
    this.marca = init.marca ?? null;
    this.C = coloresPdf(this.marca);
    this.fmtNum =
      init.formatNumber ??
      ((n, d = 2) =>
        new Intl.NumberFormat("es-ES", {
          minimumFractionDigits: d,
          maximumFractionDigits: d,
        }).format(n));
  }

  /** Loads the logo and (best-effort) embeds Sora. Call before drawing page 1. */
  async preload(): Promise<void> {
    // Con marca, SU logo (o ninguno) arriba; el de Ulture solo pequeño en el pie,
    // junto a «Hecho con Ulture Pest» (Pablo, 29-09-2026, muestra F0).
    if (this.marca) {
      this.logoDataUrl = this.marca.logo?.dataUrl ?? null;
      this.logoUltureDataUrl = await getUltureLogoDataUrl();
    } else {
      this.logoDataUrl = await getUltureLogoDataUrl();
    }
    try {
      const { registerSora } = await import("./sora");
      if (await registerSora(this.doc)) {
        this.family = "Sora";
        this.lightFamily = "SoraLight";
      }
    } catch {
      /* keep helvetica */
    }
  }

  get pageW(): number { return this.doc.internal.pageSize.getWidth(); }
  get pageH(): number { return this.doc.internal.pageSize.getHeight(); }
  /** Usable content width — adapts to portrait (188mm) or landscape (275mm). */
  get contentW(): number { return this.pageW - LEFT - PDF_V2.margin.right; }
  get cursor(): number { this.flushSection(); return this.y; }
  get fontFamily(): string { return this.family; }
  setCursor(y: number): void { this.flushSection(); this.y = y; }
  advance(dy: number): void { this.flushSection(); this.y += dy; }

  /** setFont by weight: 700→bold, 600→normal (Sora SemiBold), 400→light family. */
  private f(weight: 400 | 600 | 700): void {
    if (weight === 700) this.doc.setFont(this.family, "bold");
    else if (weight === 400) this.doc.setFont(this.lightFamily, "normal");
    else this.doc.setFont(this.family, "normal");
  }

  // ── Header band (page 1) ──────────────────────────────────────────────────
  drawGradientHeader(): void {
    if (this.marca) {
      this.drawCabeceraMarca(this.marca);
      return;
    }
    const d = this.doc;
    const w = this.contentW;
    const h = 16;
    const x = LEFT;
    const yTop = TOP;

    const png = gradientHeaderPNG(w, h, 2.6);
    if (png) {
      try { d.addImage(png, "PNG", x, yTop, w, h); } catch { this.flatHeader(x, yTop, w, h); }
    } else {
      this.flatHeader(x, yTop, w, h);
    }

    // Logo tile (white rounded square) + logo
    const tile = 10;
    const tileX = x + 4;
    const tileY = yTop + (h - tile) / 2;
    d.setFillColor(...this.C.white);
    d.roundedRect(tileX, tileY, tile, tile, 2.5, 2.5, "F");
    if (this.logoDataUrl) {
      try { d.addImage(this.logoDataUrl, "PNG", tileX + 1.5, tileY + 1.5, tile - 3, tile - 3); } catch { /* ignore */ }
    }

    // Title + subtitle (left, white)
    const textX = tileX + tile + 3;
    d.setTextColor(...this.C.white);
    this.f(700); d.setFontSize(S.brandTitle);
    d.text(this.opts.brandTitle ?? "Ulture Pest", textX, yTop + 7);
    this.f(400); d.setFontSize(S.brandSub);
    d.text(this.opts.brandSubtitle, textX, yTop + 11.6);

    // Client + date range (right, white)
    const rightX = x + w - 4;
    this.f(700); d.setFontSize(S.clientName);
    d.text(this.opts.clientName, rightX, yTop + 7, { align: "right" });
    if (this.opts.dateRange) {
      this.f(400); d.setFontSize(S.dateRange);
      d.text(this.opts.dateRange, rightX, yTop + 11.6, { align: "right" });
    }

    this.y = yTop + h + 4;
  }

  private flatHeader(x: number, y: number, w: number, h: number): void {
    const d = this.doc;
    d.setFillColor(...this.C.grad.mid);
    d.roundedRect(x, y, w, h, 2.6, 2.6, "F");
  }

  /** El logo de la marca dentro de una caja máxima, con SU proporción (nunca cuadrado a la fuerza). */
  private tamLogoMarca(maxW: number, maxH: number): { w: number; h: number } {
    const logo = this.marca?.logo;
    if (!this.logoDataUrl || !logo || logo.w <= 0 || logo.h <= 0) return { w: 0, h: 0 };
    const r = logo.w / logo.h;
    let h = maxH;
    let w = h * r;
    if (w > maxW) {
      w = maxW;
      h = w / r;
    }
    return { w, h };
  }

  /**
   * La cabecera con la marca de la empresa («Personalizar informes», 29-09-2026).
   *  - «blanca»: el logo con su proporción a la izquierda; a su lado el nombre (en
   *    su color principal, oscurecido si no se lee) y las líneas que la empresa
   *    enseña; a la derecha el informe y el periodo; debajo, una raya del acento.
   *  - «banda»: lo mismo sobre una banda del color principal, con el logo en un
   *    azulejo blanco y el texto en blanco o en tinta, según se lea.
   * La altura crece con lo que lleva: nada se pisa ni se sale.
   */
  private drawCabeceraMarca(m: MarcaInformes): void {
    const d = this.doc;
    const w = this.contentW;
    const x = LEFT;
    const yTop = TOP;
    const col = coloresMarca(m);
    // Nunca una banda negra (ni casi blanca): con ese principal, cabecera blanca.
    const banda = m.estilo === "banda" && admiteBanda(m.colorPrincipal);
    const pad = banda ? 4 : 0;
    const tilePad = banda ? 1.6 : 0;
    const logo = this.tamLogoMarca(banda ? 34 : 40, banda ? 11 : 14);

    /** Texto partido a un ancho, como mucho `max` renglones; si sobra, el último acaba en «…». */
    const recortar = (renglones: string[], ancho: number, max: number): string[] => {
      if (renglones.length <= max) return renglones;
      const out = renglones.slice(0, max);
      let ult = out[max - 1];
      while (ult.length > 1 && d.getTextWidth(`${ult}…`) > ancho) ult = ult.slice(0, -1);
      out[max - 1] = `${ult.trimEnd()}…`;
      return out;
    };
    const partir = (texto: string, ancho: number, max: number) => recortar(d.splitTextToSize(texto, ancho) as string[], ancho, max);

    // Derecha: el informe en grande, lo que va tras « · » debajo en pequeño
    // («Informe de aplicaciones» / «Registro de tratamientos») y el periodo.
    // Ocupa lo que mide su texto (como mucho el 42 %): el resto es para la empresa.
    // Todo PARTIDO dentro de su ancho: nada se monta sobre las líneas de la empresa.
    const [principalTitulo, ...resto] = this.opts.brandSubtitle.split(" · ");
    const subtituloTexto = resto.join(" · ");
    const maxRightW = w * 0.42 - pad;
    this.f(700);
    d.setFontSize(S.brandTitle);
    const titulo = partir(principalTitulo, maxRightW, 2);
    let medido = Math.max(...titulo.map((t) => d.getTextWidth(t)));
    this.f(400);
    d.setFontSize(S.dateRange);
    const subtitulo = subtituloTexto ? partir(subtituloTexto, maxRightW, 2) : [];
    for (const sLinea of subtitulo) medido = Math.max(medido, d.getTextWidth(sLinea));
    const fecha = this.opts.dateRange ? partir(this.opts.dateRange, maxRightW, 1) : [];
    for (const fLinea of fecha) medido = Math.max(medido, d.getTextWidth(fLinea));
    const rightW = Math.min(maxRightW, medido);
    const tituloLH = 4.8;
    const subLH = 3.4;
    const fechaLH = 3.4;
    const rightH = titulo.length * tituloLH + subtitulo.length * subLH + fecha.length * fechaLH;

    // Izquierda: el nombre y las líneas, al lado del logo. Cada línea en dos
    // renglones como mucho y seis en total; lo que no cabe acaba en «…».
    const textX = x + pad + (logo.w ? logo.w + tilePad * 2 + 3.5 : 0);
    const leftW = Math.max(30, x + w - pad - rightW - 6 - textX);
    this.f(700);
    d.setFontSize(S.clientName + 1.5);
    const nombre = partir(m.nombre, leftW, 2);
    const nombreLH = 4.3;
    this.f(400);
    d.setFontSize(S.chip);
    const lineas = recortar(
      m.lineas.filter((l) => l.trim()).flatMap((l) => partir(l, leftW, 2)),
      leftW,
      6,
    );
    const lineaLH = 3.0;
    const leftH = nombre.length * nombreLH + (lineas.length ? 0.8 + lineas.length * lineaLH : 0);

    const contentH = Math.max(logo.h + tilePad * 2, leftH, rightH);
    const h = Math.max(banda ? 18 : 14, contentH + pad * 2);

    const tinta = banda ? col.sobreBanda : this.C.ink;
    // Lo secundario sobre la banda, sin bajar de 4.5:1.
    const suave = banda ? textoSuaveSobre(col.principal, col.sobreBanda) : this.C.muted;

    if (banda) {
      d.setFillColor(...col.principal);
      d.roundedRect(x, yTop, w, h, 2.6, 2.6, "F");
    }

    // Logo (en su azulejo blanco si va sobre la banda).
    if (logo.w) {
      const boxW = logo.w + tilePad * 2;
      const boxH = logo.h + tilePad * 2;
      const bx = x + pad;
      const by = yTop + (h - boxH) / 2;
      if (banda) {
        d.setFillColor(...this.C.white);
        d.roundedRect(bx, by, boxW, boxH, 2.2, 2.2, "F");
      }
      try {
        d.addImage(this.logoDataUrl as string, "PNG", bx + tilePad, by + tilePad, logo.w, logo.h, LOGO_MARCA_ALIAS);
      } catch {
        /* un logo que jsPDF no entiende no tumba el informe */
      }
    }

    // Nombre y líneas.
    let ty = yTop + (h - leftH) / 2;
    this.f(700);
    d.setFontSize(S.clientName + 1.5);
    d.setTextColor(...(banda ? col.sobreBanda : col.principalTexto));
    for (const n of nombre) {
      d.text(n, textX, ty, { baseline: "top" });
      ty += nombreLH;
    }
    if (lineas.length) {
      ty += 0.8;
      this.f(400);
      d.setFontSize(S.chip);
      d.setTextColor(...suave);
      for (const l of lineas) {
        d.text(l, textX, ty, { baseline: "top" });
        ty += lineaLH;
      }
    }

    // Informe y periodo, a la derecha.
    const rightX = x + w - pad;
    let ry = yTop + (h - rightH) / 2;
    this.f(700);
    d.setFontSize(S.brandTitle);
    d.setTextColor(...tinta);
    for (const t of titulo) {
      d.text(t, rightX, ry, { align: "right", baseline: "top" });
      ry += tituloLH;
    }
    this.f(400);
    d.setFontSize(S.dateRange);
    d.setTextColor(...suave);
    for (const sLinea of subtitulo) {
      d.text(sLinea, rightX, ry, { align: "right", baseline: "top" });
      ry += subLH;
    }
    for (const fLinea of fecha) {
      d.text(fLinea, rightX, ry, { align: "right", baseline: "top" });
      ry += fechaLH;
    }

    // La raya del acento: siempre bajo la cabecera blanca; bajo la banda, solo si
    // la empresa eligió un acento DISTINTO del principal (Nijasol: banda negra, raya roja).
    let bottom = yTop + h;
    const rayaBajoBanda = banda && m.colorAcento.trim().toLowerCase() !== m.colorPrincipal.trim().toLowerCase();
    if (!banda || rayaBajoBanda) {
      d.setFillColor(...col.acento);
      d.rect(x, bottom + (banda ? 1.2 : 1.6), w, 0.7, "F");
      bottom += (banda ? 1.2 : 1.6) + 0.7;
    }
    this.y = bottom + 4;
  }

  // ── Filter chips ──────────────────────────────────────────────────────────
  drawFilterChips(chips: FilterChipV2[]): void {
    this.flushSection();
    if (!chips.length) return;
    const d = this.doc;
    const h = 6;
    const padX = 2.5;
    const gap = 2;
    let x = LEFT;
    let y = this.y;
    const maxX = LEFT + this.contentW;

    this.f(600); d.setFontSize(S.chip);
    chips.forEach((chip) => {
      const text = `${chip.label}: ${chip.value}`;
      const cw = d.getTextWidth(text) + padX * 2;
      if (x + cw > maxX) { x = LEFT; y += h + 2; }
      d.setDrawColor(...this.C.line);
      d.setLineWidth(0.3);
      d.roundedRect(x, y, cw, h, h / 2, h / 2, "D");
      d.setTextColor(...this.C.muted);
      d.text(text, x + padX, y + h / 2 + 0.1, { baseline: "middle" });
      x += cw + gap;
    });
    this.y = y + h + 3;
  }

  // ── KPI cards (4-up, left tick) ───────────────────────────────────────────
  drawKpiCards(cards: KpiCard[]): void {
    this.flushSection();
    if (!cards.length) return;
    const d = this.doc;
    const n = Math.min(cards.length, 4);
    const gap = 3;
    const w = (this.contentW - gap * (n - 1)) / n;
    const h = 13;

    cards.slice(0, 4).forEach((card, i) => {
      const x = LEFT + i * (w + gap);
      const y = this.y;
      // Card
      d.setFillColor(...this.C.white);
      d.setDrawColor(...this.C.line);
      d.setLineWidth(0.3);
      d.roundedRect(x, y, w, h, 2.4, 2.4, "FD");
      // Left tick
      d.setFillColor(...this.C.kpiTick[i % this.C.kpiTick.length]);
      d.roundedRect(x, y + 1, 1.2, h - 2, 0.6, 0.6, "F");
      // Label
      this.f(600); d.setFontSize(S.kpiLabel);
      d.setTextColor(...this.C.muted);
      d.text(card.label.toUpperCase(), x + 3.5, y + 4);
      // Value
      this.f(700); d.setFontSize(S.kpiValue);
      d.setTextColor(...(card.valueTone === "green" ? this.C.valBio : card.valueTone === "amber" ? this.C.valChem : this.C.ink));
      d.text(card.value, x + 3.5, y + 9);
      // Note
      if (card.note) {
        this.f(400); d.setFontSize(S.kpiNote);
        d.setTextColor(...this.C.muted);
        d.text(card.note, x + 3.5, y + 11.8);
      }
    });

    this.y += h + 5;
  }

  // ── Day section header ────────────────────────────────────────────────────
  /** Alto de la cabecera de sección (barra + título + aire hasta el bloque). */
  private static readonly SECTION_H = 7;

  /** Se pinta junto con el bloque siguiente (ver `pendingSection`). */
  drawDaySection(dateLabel: string, pillText: string): void {
    this.flushSection();
    this.pendingSection = { title: dateLabel, pill: pillText };
  }

  /**
   * Pinta la cabecera pendiente. `blockH` = lo que mide el bloque que va
   * debajo: si cabecera + bloque no caben en lo que queda, salto de página
   * ANTES (salvo que ya estemos arriba del todo: un bloque más alto que una
   * página se parte sí o sí, pero al menos empieza en una hoja nueva). Sin
   * medida, el mínimo de siempre (cabecera + parte del primer bloque).
   */
  private flushSection(blockH = 13): void {
    const s = this.pendingSection;
    if (!s) return;
    this.pendingSection = null;
    this.ensureSpace(PdfReportBuilderV2.SECTION_H + blockH);
    this.paintDaySection(s.title, s.pill);
  }

  private paintDaySection(dateLabel: string, pillText: string): void {
    const d = this.doc;
    const y = this.y;
    // Teal vertical bar
    d.setFillColor(...this.C.teal);
    d.rect(LEFT, y, 0.9, 5, "F");
    // Date
    this.f(700); d.setFontSize(S.dayDate);
    d.setTextColor(...this.C.ink);
    d.text(dateLabel, LEFT + 3, y + 4.1);
    const dateW = d.getTextWidth(dateLabel);
    // Count pill
    this.f(700); d.setFontSize(S.dayPill);
    const pillPadX = 2.2;
    const pillH = 4;
    const pillW = d.getTextWidth(pillText) + pillPadX * 2;
    const pillX = LEFT + 3 + dateW + 3;
    const pillY = y + 0.3;
    d.setFillColor(...this.C.tealSoft);
    d.roundedRect(pillX, pillY, pillW, pillH, pillH / 2, pillH / 2, "F");
    d.setTextColor(...this.C.tealD);
    d.text(pillText, pillX + pillPadX, pillY + pillH / 2 + 0.1, { baseline: "middle" });

    this.y = y + PdfReportBuilderV2.SECTION_H;
  }

  // ── Application block ─────────────────────────────────────────────────────
  /** Conservative height estimate (mm) for pagination decisions. */
  measureApplicationBlock(block: ApplicationBlock): number {
    const headBand = 7;
    const colHeader = 5;
    const rowH = 5.4;
    return headBand + this.contextLineLayout(block).height + colHeader + block.rows.length * rowH + 3;
  }

  /**
   * La línea de contexto del bloque (máquina · aplicador · tiempo · eficacia,
   * F123): partida al ancho del bloque, como mucho en DOS renglones (lo que no
   * quepa se corta con «…»). Sin línea, alto 0 y el bloque es el de siempre.
   */
  private contextLineLayout(block: ApplicationBlock): { lines: string[]; height: number } {
    const text = block.contextLine?.trim();
    if (!text) return { lines: [], height: 0 };
    const d = this.doc;
    this.f(400);
    d.setFontSize(S.pest);
    const maxW = this.contentW - 5.2;
    let lines = d.splitTextToSize(text, maxW) as string[];
    if (lines.length > 2) {
      let second = lines[1];
      while (second.length > 1 && d.getTextWidth(`${second}…`) > maxW) second = second.slice(0, -1);
      lines = [lines[0], `${second.trimEnd()}…`];
    }
    const lineH = 3.1;
    return { lines, height: lines.length * lineH + 1.6 };
  }

  /** Page-break (no header on continuation pages) if the block won't fit. */
  private ensureBlockFits(h: number, keepWith = 0): void {
    if (this.y + h + keepWith > this.pageH - PDF_V2.margin.bottom && this.y > TOP) {
      this.doc.addPage();
      this.y = TOP;
    }
  }

  /** Solid pill (badge). Returns its width so callers can advance x. */
  private pill(x: number, yMid: number, label: string, bg: number[], fg: number[]): number {
    const d = this.doc;
    this.f(700); d.setFontSize(S.badge);
    const padX = 2.2;
    const h = 3.4;
    const w = d.getTextWidth(label) + padX * 2;
    d.setFillColor(bg[0], bg[1], bg[2]);
    d.roundedRect(x, yMid - h / 2, w, h, h / 2, h / 2, "F");
    d.setTextColor(fg[0], fg[1], fg[2]);
    d.text(label, x + padX, yMid + 0.1, { baseline: "middle" });
    return w;
  }

  drawApplicationBlock(block: ApplicationBlock): void {
    this.flushSection(this.measureApplicationBlock(block));
    this.ensureBlockFits(this.measureApplicationBlock(block));
    const d = this.doc;
    const startY = this.y;
    const innerL = LEFT + 2.6;
    const innerR = LEFT + this.contentW - 2.6;
    const headH = 7;
    const yMid = startY + headH / 2;

    // Head band tint
    d.setFillColor(...this.C.appTint);
    d.rect(LEFT, startY, this.contentW, headH, "F");

    // Left: location path
    this.f(700); d.setFontSize(S.locPath);
    d.setTextColor(...this.C.ink);
    d.text(block.locationPath, innerL, yMid + 0.1, { baseline: "middle" });
    let xL = innerL + d.getTextWidth(block.locationPath) + 3;

    // Badge
    if (block.mode) {
      const isBio = block.mode === "bio";
      const label = block.modeLabel ?? (isBio ? "BIOLÓGICO" : "QUÍMICO");
      const bw = this.pill(xL, yMid, label, isBio ? this.C.bioBg : this.C.amberBg, isBio ? this.C.bioTx : this.C.amberTx);
      xL += bw + 3;
    }

    // Pest (red dot + name) or placeholder
    if (block.targetPest) {
      d.setFillColor(...this.C.pestDot);
      d.circle(xL + 0.7, yMid, 0.7, "F");
      this.f(600); d.setFontSize(S.pest);
      d.setTextColor(...this.C.secondary);
      d.text(block.targetPest, xL + 2.2, yMid + 0.1, { baseline: "middle" });
    } else if (block.placeholderText) {
      this.f(400); d.setFontSize(S.pest);
      d.setTextColor(...this.C.placeholder);
      d.text(block.placeholderText, xL, yMid + 0.1, { baseline: "middle" });
    }

    // Right: green meta line (ha · €/ha · subtotal), drawn right-to-left.
    // Sin subtotal (cifras apagadas) no hay dinero que pintar: solo las ha.
    let xR = innerR;
    // Precio pendiente (I17): el subtotal es un mínimo y se dice, en el ámbar de
    // «pendiente» de toda la app (el del rótulo Químico, que la marca no cambia).
    const incomplete = block.meta.incomplete;
    if (block.meta.subtotal != null) {
      const subtotal = incomplete ? incomplete.subtotalText : this.opts.formatCurrency(block.meta.subtotal);
      this.f(700); d.setFontSize(S.appSubtotal);
      d.setTextColor(...(incomplete ? this.C.amberTx : this.C.greenD));
      d.text(subtotal, xR, yMid + 0.1, { align: "right", baseline: "middle" });
      xR -= d.getTextWidth(subtotal) + 4;
    }
    this.f(600); d.setFontSize(S.appMeta);
    d.setTextColor(...this.C.green);
    const perHa =
      block.meta.costPerHa != null
        ? `${this.opts.formatCurrency(block.meta.costPerHa)}/ha`
        : incomplete?.costPerHaText ?? null;
    if (perHa) {
      d.text(perHa, xR, yMid + 0.1, { align: "right", baseline: "middle" });
      xR -= d.getTextWidth(perHa) + 3.5;
    }
    if (block.meta.ha != null) {
      const s = `${this.fmtNum(block.meta.ha, 2)} ha`;
      d.text(s, xR, yMid + 0.1, { align: "right", baseline: "middle" });
    }

    // Línea de contexto (F123): máquina · aplicador · tiempo · eficacia, en gris,
    // entre la banda y la tabla. La tabla empieza DEBAJO: no se pisa.
    const ctx = this.contextLineLayout(block);
    if (ctx.lines.length) {
      this.f(400); d.setFontSize(S.pest);
      d.setTextColor(...this.C.materia);
      ctx.lines.forEach((ln, i) => {
        d.text(ln, innerL, startY + headH + 1.0 + i * 3.1, { baseline: "top" });
      });
    }

    // Product table
    const tableY = startY + headH + ctx.height;
    const tableW = this.contentW - 5.2;
    const numericIdx = new Set<number>();
    const mutedIdx = new Set<number>();
    const columnStyles: UserOptions["columnStyles"] = {};
    const explicitBold = block.columns.some((c) => c.bold !== undefined);
    block.columns.forEach((col, i) => {
      const right = col.align === "right";
      if (right) numericIdx.add(i);
      if (col.muted) mutedIdx.add(i);
      const bold = explicitBold ? col.bold === true : i === block.columns.length - 1;
      columnStyles[i] = {
        ...(col.width ? { cellWidth: col.width } : {}),
        halign: right ? "right" : "left",
        ...(bold ? { fontStyle: "bold" } : {}),
      };
    });

    autoTable(d, {
      startY: tableY,
      head: [block.columns.map((c) => c.header.toUpperCase())],
      body: block.rows,
      theme: "plain",
      tableWidth: tableW,
      margin: { left: innerL, right: PDF_V2.margin.right + 2.6 },
      styles: {
        font: this.family,
        fontStyle: "normal",
        fontSize: S.num,
        textColor: this.C.ink,
        cellPadding: { top: 1.2, right: 2.6, bottom: 1.2, left: 2.6 },
        lineColor: this.C.lineSoft,
        lineWidth: { bottom: 0.2 },
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        font: this.family,
        fontStyle: "bold",
        fontSize: S.colHeader,
        textColor: this.C.ink,
        lineColor: this.C.line,
        lineWidth: { bottom: 0.3 },
        cellPadding: { top: 1.4, right: 2.6, bottom: 1.0, left: 2.6 },
      },
      columnStyles,
      // Muted columns lighter; numeric headers AND cells right-aligned.
      didParseCell: (data) => {
        if (mutedIdx.has(data.column.index) && data.section === "body") {
          data.cell.styles.textColor = this.C.materia;
        }
        if (numericIdx.has(data.column.index)) {
          data.cell.styles.halign = "right";
        }
      },
      rowPageBreak: "avoid",
    });
    // @ts-expect-error lastAutoTable injected by the plugin
    const finalY = this.doc.lastAutoTable?.finalY ?? tableY;

    // Rounded block border around head + table
    d.setDrawColor(...this.C.line);
    d.setLineWidth(0.3);
    d.roundedRect(LEFT, startY, this.contentW, finalY - startY, 2.4, 2.4, "D");

    this.y = finalY + 2.5;
  }

  // ── Day total row ─────────────────────────────────────────────────────────
  drawDayTotalRow(label: string, value: string): void {
    this.flushSection(7);
    this.ensureSpace(7);
    const d = this.doc;
    const y = this.y;
    const h = 6;
    const rightX = LEFT + this.contentW;
    this.f(600); d.setFontSize(S.dayTotalLabel);
    d.setTextColor(...this.C.muted);
    const labelW = d.getTextWidth(label.toUpperCase());
    this.f(700); d.setFontSize(S.dayTotalValue);
    const valueW = d.getTextWidth(value);
    // value flush right; label to its left
    d.setTextColor(...this.C.ink);
    d.text(value, rightX, y + h / 2 + 0.1, { align: "right", baseline: "middle" });
    this.f(600); d.setFontSize(S.dayTotalLabel);
    d.setTextColor(...this.C.muted);
    d.text(label.toUpperCase(), rightX - valueW - 4, y + h / 2 + 0.1, { align: "right", baseline: "middle" });
    void labelW;
    this.y = y + h + 3;
  }

  // ── Generic table (B-archetype reports) ───────────────────────────────────
  drawTable(opts: TableOpts): void {
    const options = this.tableOptions(opts);
    const h = this.measureTable(options);
    // Con cabecera pendiente, las dos van juntas; sin ella, la tabla sola
    // tampoco empieza en una página donde no cabe.
    if (this.pendingSection) this.flushSection(h);
    else this.ensureSpace(h);
    autoTable(this.doc, { ...options, startY: this.y });
    // @ts-expect-error lastAutoTable injected by the plugin
    this.y = (this.doc.lastAutoTable?.finalY ?? this.y) + 6;
  }

  /**
   * Alto EXACTO de la tabla (cabecera + filas), sin pintarla: `__createTable`
   * monta el modelo de autotable con los anchos y altos de fila ya resueltos
   * (las filas que parten línea incluidas), que es justo lo que se va a pintar.
   */
  private measureTable(options: UserOptions): number {
    try {
      const t = __createTable(this.doc, { ...options, startY: this.y });
      return t.getHeadHeight(t.columns) + t.body.reduce((a, r) => a + r.height, 0);
    } catch {
      return 13; // si el modelo falla, el comportamiento de antes
    }
  }

  private tableOptions(opts: TableOpts): UserOptions {
    return {
      head: opts.head,
      body: opts.body,
      theme: "plain",
      tableWidth: this.contentW,
      margin: { left: LEFT, right: PDF_V2.margin.right },
      styles: {
        font: this.family,
        fontStyle: "normal",
        fontSize: S.num,
        textColor: this.C.ink,
        cellPadding: { top: 1.4, right: 2.4, bottom: 1.4, left: 2.4 },
        lineColor: this.C.lineSoft,
        lineWidth: { bottom: 0.2 },
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        font: this.family,
        fontStyle: "bold",
        fontSize: S.colHeader,
        textColor: this.C.ink,
        lineColor: this.C.line,
        lineWidth: { bottom: 0.3 },
        halign: "left",
      },
      columnStyles: opts.columnStyles,
      didParseCell: opts.didParseCell,
    };
  }

  // ── Footer on every page ──────────────────────────────────────────────────
  drawFootersOnAllPages(): void {
    this.flushSection();
    if (this.marca) {
      this.drawPiesMarca();
      return;
    }
    const d = this.doc;
    const total = d.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      d.setPage(p);
      const y = this.pageH - 8;
      this.f(400); d.setFontSize(S.footer);
      d.setTextColor(...this.C.footer);
      d.text(this.opts.footer.generated, LEFT, y);
      this.f(700);
      d.text("Ulture Pest", this.pageW / 2, y, { align: "center" });
      this.f(400);
      d.text(`${this.opts.footer.page} ${p} ${this.opts.footer.of} ${total}`, LEFT + this.contentW, y, { align: "right" });
    }
  }

  /**
   * El pie de un informe CON marca: su logo pequeño en TODAS las páginas (se
   * incrusta una vez, por alias), la fecha, «Hecho con Ulture Pest» pequeño y en
   * gris (decisión de Pablo, 29-09-2026) y la página.
   */
  private drawPiesMarca(): void {
    const d = this.doc;
    const total = d.getNumberOfPages();
    const credito = this.opts.footer.madeWith ?? "Hecho con Ulture Pest";
    const logo = this.tamLogoMarca(14, 3.6);
    for (let p = 1; p <= total; p++) {
      d.setPage(p);
      const y = this.pageH - 8;
      d.setDrawColor(...this.C.lineSoft);
      d.setLineWidth(0.2);
      d.line(LEFT, y - 4.4, LEFT + this.contentW, y - 4.4);
      let xL = LEFT;
      if (logo.w) {
        try {
          d.addImage(this.logoDataUrl as string, "PNG", LEFT, y - logo.h + 0.7, logo.w, logo.h, LOGO_MARCA_ALIAS);
          xL += logo.w + 2;
        } catch {
          /* sin logo en el pie, el resto igual */
        }
      }
      this.f(400);
      d.setFontSize(S.footer);
      d.setTextColor(...this.C.footer);
      d.text(this.opts.footer.generated, xL, y);
      // «[logo] Hecho con Ulture Pest», centrado como un todo.
      const icono = this.logoUltureDataUrl ? 3 : 0;
      const creditoW = d.getTextWidth(credito);
      const cx = this.pageW / 2 - (icono ? icono + 1.2 : 0) / 2 - creditoW / 2 + (icono ? icono + 1.2 : 0);
      if (icono) {
        try {
          d.addImage(this.logoUltureDataUrl as string, "PNG", cx - icono - 1.2, y - icono + 0.6, icono, icono, LOGO_ULTURE_ALIAS);
        } catch {
          /* sin el icono, el texto igual */
        }
      }
      d.text(credito, cx, y);
      d.text(`${this.opts.footer.page} ${p} ${this.opts.footer.of} ${total}`, LEFT + this.contentW, y, { align: "right" });
    }
  }

  // ── Utility ───────────────────────────────────────────────────────────────
  ensureSpace(need: number): void {
    // Quien reserva sitio justo tras una cabecera de sección (los informes que
    // pintan a mano después) la lleva consigo: cabecera + lo suyo, juntos.
    if (this.pendingSection) {
      this.flushSection(need);
      return;
    }
    if (this.y + need > this.pageH - PDF_V2.margin.bottom && this.y > TOP) {
      this.doc.addPage();
      this.y = TOP;
    }
  }

  static formatDateRange(start: string, end: string, locale = "es"): string {
    const fmt = (s: string) => {
      const [y, m, dd] = s.split("-").map(Number);
      return new Date(y, (m ?? 1) - 1, dd ?? 1).toLocaleDateString(locale, {
        day: "2-digit", month: "short", year: "numeric",
      });
    };
    return `${fmt(start)} – ${fmt(end)}`;
  }
}
